import { IconButton, Modal, Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCurrentCampusId, useCurrentOrgId } from 'shared-state/location/hook';
import { useLocalProfile } from 'shared-state/identity/hooks';

interface ExternalDoorsIframeModalProps {
    onClose: () => void;
    urlPath: string; // e.g., '' for AccessManagement, '/calendar' for Calendar
    title: string;
    modalPadding?: number; // Optional padding for modal (default: 10)
}

const ExternalDoorsIframeModal: React.FC<ExternalDoorsIframeModalProps> = ({ onClose, urlPath, title, modalPadding = 10 }) => {
    const [isModalOpen, { setTrue: openModal, setFalse: closeModal }] = useBoolean(false);
    const [campusId] = useCurrentCampusId();
    const [orgId] = useCurrentOrgId();
    const currentUser = useLocalProfile();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Memoize language detection
    const chosenLang = useMemo(() => localStorage.getItem('chosenLanguage') || (navigator.language.includes('de') ? 'de' : 'en') || 'en', []);

    const accessToken = localStorage.getItem('ivAccessToken') || '';
    const baseUrl = process.env.REACT_APP_ACCESS_MANAGEMENT_URL || '';

    // Get allowed origin from env for security
    const allowedOrigin = useMemo(() => {
        if (!baseUrl) return null;
        try {
            return new URL(baseUrl).origin;
        } catch {
            return null;
        }
    }, [baseUrl]);

    const iframeUrl = useMemo(
        () =>
            baseUrl
                ? `${baseUrl}${urlPath}?lang=${encodeURIComponent(chosenLang)}&accessToken=${encodeURIComponent(accessToken)}&campusId=${encodeURIComponent(
                      campusId || ''
                  )}&orgId=${encodeURIComponent(orgId || '')}`
                : null,
        [baseUrl, urlPath, chosenLang, accessToken, campusId, orgId]
    );

    const handleClose = useCallback(() => {
        closeModal();
        onClose();
    }, [closeModal, onClose]);

    const sendInitData = useCallback(() => {
        if (iframeRef.current?.contentWindow && currentUser?.uid && allowedOrigin) {
            iframeRef.current.contentWindow.postMessage(
                {
                    type: 'INIT_DATA',
                    currentUser
                },
                allowedOrigin // Use specific origin instead of '*' for security
            );
        }
    }, [currentUser, allowedOrigin]);

    // Auto-open modal on mount
    useEffect(() => {
        openModal();
    }, []);

    // Send data to iframe when user data is available
    useEffect(() => {
        sendInitData();
    }, [sendInitData]);

    // Listen for messages from iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Verify origin for security
            if (allowedOrigin && event.origin !== allowedOrigin) return;

            if (event.data?.type === 'MODAL_CLOSED') {
                const modal = event.data.modal;
                if (modal === 'access-pass-link-ready' || modal === 'access-pass-link-cancel') {
                    handleClose();
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [allowedOrigin, handleClose]);

    const handleIframeLoad = useCallback(() => {
        sendInitData();
    }, [sendInitData]);

    return (
        <Modal
            isOpen={isModalOpen}
            onDismiss={handleClose}
            isBlocking={false}
            styles={{ main: { borderRadius: 10, padding: modalPadding, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack style={{ padding: 5 }}>
                <IconButton style={{ marginLeft: 'auto' }} iconProps={{ iconName: 'Cancel' }} onClick={handleClose} />
            </Stack>
            <Stack style={{ width: '75vw', maxWidth: 1200, height: '70vh' }}>
                {iframeUrl ? (
                    <iframe
                        ref={iframeRef}
                        src={iframeUrl}
                        frameBorder="0"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={title}
                        aria-label={`${title} iframe`}
                        onLoad={handleIframeLoad}
                        allow="clipboard-write"
                    />
                ) : (
                    <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '100%', padding: 20 }}>
                        <p>{title} URL not configured. Please set REACT_APP_ACCESS_MANAGEMENT_URL environment variable.</p>
                    </Stack>
                )}
            </Stack>
        </Modal>
    );
};

export default ExternalDoorsIframeModal;
